import React, { useState } from 'react';
import { FormField } from 'grommet';
import { TextInputBorderless, StyledTextInput } from './SHDInput.styles';

// Helpers and utilities
import { generateRandomString } from '@theme/utils/data';

// Types
import type { SHDInputProps } from './SHDInput.d';

const id = `input__${generateRandomString(6)}`;

const SHDInput: React.FC<SHDInputProps> = ({
  className = 'input-text',
  error = null,
  isBorderless = false,
  label,
  onChange,
  onFocusOut,
  ...props
}) => {
  // States
  const [value, setValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Email validation regex
  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Handlers
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    onChange?.(newValue);
  };

  const handleOnBlur = () => {
    if (props.autoComplete === 'email') {
      const isValid = isValidEmail(value);

      if (!isValid && value.length > 0) {
        setErrorMessage('Please enter a valid email address.');
      } else {
        setErrorMessage(null);
      }

      if (onFocusOut) {
        onFocusOut(isValid);
      }
    }
  };

  // Renderers
  const renderTextInput = () => {
    if (isBorderless) {
      return (
        <div className={`${className} full-width`}>
          <TextInputBorderless
            a11yTitle={`${label}`}
            className={`${className}__input`}
            name={id}
            id={id}
            placeholder={label}
            onChange={handleOnChange}
            onBlur={handleOnBlur} // ✅ Validate onBlur
            value={value}
            {...props}
          />
          {errorMessage && (
            <p style={{ color: 'red', fontSize: '12px' }}>{errorMessage}</p>
          )}
        </div>
      );
    }

    return (
      <div className={`${className}`} style={{ width: '100%' }}>
        <FormField
          className={`${className}__label`}
          error={errorMessage || error?.message}
          htmlFor={id}
          label={label}
        >
          <StyledTextInput
            a11yTitle={label}
            className={`${className}__input${props.readOnly ? ' is-read-only' : ''}`}
            name={id}
            id={id}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            value={value}
            {...props}
          />
        </FormField>
      </div>
    );
  };

  return <>{renderTextInput()}</>;
};

export default SHDInput;
